/**
 * Session cache for a site's ranked DCT coverage match — the
 * `matchUploadedDocsToChecklistRanked` result for each report type in scope.
 *
 * One page-level run (`useSiteCoverageMatch`, mounted by SiteLandingPage) feeds
 * the Site Evidence CoverageMatrix, the Author-evidence lineage prefill and
 * Path to GO's `coverageMatchByType`. The matcher scores every
 * document × checklist-item pair through DM2 — a Firestore read per pair, plus
 * a Gemini call for a pair never scored — so repeating it on every site open is
 * the cost this cache removes. Two layers: memory (in-app navigation) and
 * sessionStorage (a reload in the same tab). ONE entry per site: a newer result
 * replaces the older one, so the store cannot grow with every upload.
 *
 * The key names every site and document input the matcher reads:
 *   - the build — a deploy can change the checklists, the matcher or DM2's model
 *   - the site id
 *   - the report types in scope, in order (already resolved for the decision stage)
 *   - the documents the matcher scores (not soft-deleted, not the library index),
 *     IN ORDER — equal scores keep document order when Primary roles are assigned —
 *     each by id, `contentHash`, file name, body length (extraction landing) and
 *     content-index requirement ids (the Primary-exclusivity exception)
 *   - the DM5 disconnects (user-curated document ↔ requirement unlinks)
 * DM2's Firestore score rows are NOT in the key: a row written after the run
 * (eager upload scoring) is read by the next run, which starts when any keyed
 * input changes or the tab session ends.
 *
 * A run in which some pairs failed transiently — a DM2 timeout or API error,
 * a rate limit included (`RankedChecklistMatch.transientFailures`) — is stored
 * but is not final. It stays the site's latest entry (the interim map after a
 * remount reads it) and an exact-key hit until its re-run is due; after that
 * the exact-key read misses, so the next mount re-scores. Only the failed
 * pairs call Gemini again: every verdict the run got is a DM2 Firestore hit.
 * The first re-run is due `COVERAGE_RERUN_BASE_DELAY_MS` (60 s, the proxy's
 * `Retry-After`) after the failed run, doubling per re-run that fails again;
 * after `COVERAGE_MAX_RERUNS` the partial result stands for this page load. A
 * reload restores the entry from sessionStorage with a fresh budget, due at
 * once.
 */
import type { ReportTypeId } from '../config/reportTypes';
import {
  getDocumentChecklistFor,
  type ChecklistItem,
  type RankedChecklistMatch,
  type RankedDocMatch,
} from '../config/documentChecklists';
import type { SiteDocument } from '../types/siteRag';
import { isLibraryIndexDoc } from '../services/knowledge/libraryIndex';
import { documentContentRequirementIds } from '../services/knowledge/documentTopics';
import { documentBodyText } from './documentBody';
import { getBuildSha } from './runtimeFlags';

const SESSION_PREFIX = 'da-site-coverage-match:';

/** Inputs the ranked matcher reads for one site. */
export interface SiteCoverageMatchKeyInput {
  siteId: string;
  documents: readonly SiteDocument[];
  disconnectedLinks?: ReadonlyArray<{ docId: string; targetId: string }>;
  /** The resolved coverage scope, in the order the matcher walks it. */
  reportTypes: readonly ReportTypeId[];
}

/**
 * JSON form of one report type's match. Checklist items travel as ids: an item
 * can carry an `evidenceProbe` function, which JSON silently drops, so items are
 * rebuilt from the live checklist on read.
 */
interface SerializedMatch {
  matched: Array<[string, RankedDocMatch[]]>;
  missing: string[];
  satisfied: string[];
  primaryClaimCount: Array<[string, number]>;
  /** Present only when > 0; an entry from an older build has none (final). */
  transientFailures?: number;
}

interface StoredEntry {
  key: string;
  matchByType: Array<[ReportTypeId, SerializedMatch]>;
}

/**
 * Wait before a run with transient failures is re-run: the proxy's
 * `Retry-After` (60 s), so the re-run does not land in the same closed
 * rate-limit window. Doubles for each re-run that fails again.
 */
export const COVERAGE_RERUN_BASE_DELAY_MS = 60_000;

/**
 * Re-runs one page load allows for the same inputs after a run with transient
 * failures. After the last, the partial result stands until the inputs change
 * or the page reloads, so a pair that fails every time cannot re-score on
 * every visit.
 */
export const COVERAGE_MAX_RERUNS = 3;

/** Re-run schedule of a stored run with transient failures (memory only). */
interface RerunState {
  /** Runs under this key that had failures (1 after the first). */
  runs: number;
  /** Epoch ms from which the exact-key read misses, so a mount re-scores. */
  retryAt: number;
}

type CacheEntry = {
  key: string;
  matchByType: Map<ReportTypeId, RankedChecklistMatch>;
  /** Present only for a run with transient failures. */
  rerun?: RerunState;
};

const memory = new Map<string, CacheEntry>();

/** Transiently failed (doc, item) pairs across every report type of a result. */
export function coverageTransientFailures(
  matchByType: ReadonlyMap<ReportTypeId, RankedChecklistMatch>,
): number {
  let total = 0;
  for (const match of matchByType.values()) total += match.transientFailures ?? 0;
  return total;
}

/** The documents the ranked matcher scores — the same filter it applies. */
export function scoredCoverageDocuments(documents: readonly SiteDocument[]): SiteDocument[] {
  return documents.filter(d => !isLibraryIndexDoc(d) && !d.deletedAt);
}

/**
 * Content-index requirement ids, or a fixed marker for an index the matcher
 * cannot read either (it throws on that document, and the run fails closed).
 */
function contentRequirementIdsForKey(doc: SiteDocument): string[] {
  try {
    return documentContentRequirementIds(doc).sort();
  } catch {
    return ['!unreadable-content-index'];
  }
}

/** Cache key over every matcher input (see the module docstring). */
export function buildSiteCoverageMatchKey(input: SiteCoverageMatchKeyInput): string {
  const documents = scoredCoverageDocuments(input.documents).map(d => [
    d.id,
    d.contentHash ?? null,
    d.fileName ?? null,
    documentBodyText(d).length,
    contentRequirementIdsForKey(d),
  ]);
  const disconnects = (input.disconnectedLinks ?? [])
    .map(link => `${link.docId}::${link.targetId}`)
    .sort();
  return JSON.stringify([getBuildSha(), input.siteId, input.reportTypes, documents, disconnects]);
}

function serializeMatch(match: RankedChecklistMatch): SerializedMatch {
  return {
    matched: [...match.matched],
    missing: match.missing.map(item => item.id),
    satisfied: match.satisfied.map(item => item.id),
    primaryClaimCount: [...match.primaryClaimCount],
    ...(match.transientFailures ? { transientFailures: match.transientFailures } : {}),
  };
}

/** Undefined when an item id no longer exists — the entry predates a checklist change. */
function deserializeMatch(typeId: ReportTypeId, raw: SerializedMatch): RankedChecklistMatch | undefined {
  const items = new Map(getDocumentChecklistFor(typeId).map(item => [item.id, item]));
  const resolve = (ids: readonly string[]): ChecklistItem[] | undefined => {
    const out: ChecklistItem[] = [];
    for (const id of ids) {
      const item = items.get(id);
      if (!item) return undefined;
      out.push(item);
    }
    return out;
  };
  const missing = resolve(raw.missing);
  const satisfied = resolve(raw.satisfied);
  if (!missing || !satisfied) return undefined;
  return {
    matched: new Map(raw.matched),
    missing,
    satisfied,
    primaryClaimCount: new Map(raw.primaryClaimCount),
    ...(raw.transientFailures ? { transientFailures: raw.transientFailures } : {}),
  };
}

function readSession(siteId: string): CacheEntry | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(`${SESSION_PREFIX}${siteId}`);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as StoredEntry;
    const out = new Map<ReportTypeId, RankedChecklistMatch>();
    for (const [typeId, match] of stored.matchByType) {
      const parsed = deserializeMatch(typeId, match);
      if (!parsed) return undefined;
      out.set(typeId, parsed);
    }
    // A reload starts a fresh re-run budget, due at once.
    return coverageTransientFailures(out) > 0
      ? { key: stored.key, matchByType: out, rerun: { runs: 0, retryAt: 0 } }
      : { key: stored.key, matchByType: out };
  } catch {
    return undefined;
  }
}

/** The site's stored entry — memory, then sessionStorage — whatever its key. */
function storedEntry(siteId: string): CacheEntry | undefined {
  const mem = memory.get(siteId);
  if (mem) return mem;
  const fromSession = readSession(siteId);
  if (fromSession) memory.set(siteId, fromSession);
  return fromSession;
}

function writeSession(siteId: string, key: string, entry: Map<ReportTypeId, RankedChecklistMatch>): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const stored: StoredEntry = {
      key,
      matchByType: [...entry].map(([typeId, match]) => [typeId, serializeMatch(match)]),
    };
    sessionStorage.setItem(`${SESSION_PREFIX}${siteId}`, JSON.stringify(stored));
  } catch {
    // Quota / private mode — the memory layer still serves this tab session.
    // Drop the site's older entry rather than leave it holding shared quota.
    try {
      sessionStorage.removeItem(`${SESSION_PREFIX}${siteId}`);
    } catch {
      /* swallow */
    }
  }
}

/** True when a run with transient failures should be scored again now. */
function rerunDue(entry: CacheEntry, now: number): boolean {
  return !!entry.rerun && entry.rerun.runs <= COVERAGE_MAX_RERUNS && now >= entry.rerun.retryAt;
}

/**
 * Synchronous read (memory, then sessionStorage) for the entry under `key`.
 * Misses for a run with transient failures once its re-run is due, so the
 * caller scores again (see the module docstring).
 */
export function getCachedSiteCoverageMatch(
  siteId: string,
  key: string,
  now: number = Date.now(),
): Map<ReportTypeId, RankedChecklistMatch> | undefined {
  const entry = storedEntry(siteId);
  if (entry?.key !== key || rerunDue(entry, now)) return undefined;
  return entry.matchByType;
}

/**
 * The site's most recent stored result, WHATEVER inputs produced it. A page
 * that has just (re)mounted keeps no result of its own, so the interim map for
 * a run in flight starts from this one; callers filter it to the pending
 * inputs (live documents, disconnects, scope) before showing it.
 */
export function getLatestCachedSiteCoverageMatch(
  siteId: string,
): Map<ReportTypeId, RankedChecklistMatch> | undefined {
  return storedEntry(siteId)?.matchByType;
}

/**
 * Store a completed run, replacing the site's previous entry. A run with
 * transient failures is scheduled for a re-run: the delay doubles for each
 * consecutive run under the same key that still had failures.
 */
export function setCachedSiteCoverageMatch(
  siteId: string,
  key: string,
  entry: Map<ReportTypeId, RankedChecklistMatch>,
  now: number = Date.now(),
): void {
  let rerun: RerunState | undefined;
  if (coverageTransientFailures(entry) > 0) {
    const previous = storedEntry(siteId);
    const runs = (previous?.key === key ? previous.rerun?.runs ?? 0 : 0) + 1;
    rerun = { runs, retryAt: now + COVERAGE_RERUN_BASE_DELAY_MS * 2 ** (runs - 1) };
  }
  memory.set(siteId, rerun ? { key, matchByType: entry, rerun } : { key, matchByType: entry });
  writeSession(siteId, key, entry);
}

/** Test hook — reset both layers. */
export function clearSiteCoverageMatchCacheForTests(): void {
  memory.clear();
  if (typeof sessionStorage === 'undefined') return;
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(SESSION_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}

/** Test hook — memory layer only (sessionStorage survives, as on a reload). */
export function clearSiteCoverageMatchMemoryForTests(): void {
  memory.clear();
}
